import { useSyncExternalStore } from 'react'

import { StorageKeys } from '../../../constants/StorageKeys'

/*
 * Google Maps sends visitor data (IP address) to Google, so the map is only loaded after explicit consent.
 * Consent is kept in memory for the current visit and, if the visitor asks to remember it, in localStorage.
 */

const readStoredConsent = (): boolean => {
  try {
    return window.localStorage.getItem(StorageKeys.MapConsent) === 'true'
  } catch (error) {
    return false
  }
}

let state = {
  granted: readStoredConsent(),
  remembered: readStoredConsent(),
}
const listeners = new Set<() => void>()

const setState = (next: typeof state) => {
  state = next
  listeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

const getSnapshot = () => state

export const useMapConsent = () => {
  const { granted, remembered } = useSyncExternalStore(subscribe, getSnapshot)

  const grant = (remember: boolean) => {
    if (remember) {
      try {
        window.localStorage.setItem(StorageKeys.MapConsent, 'true')
      } catch (error) {
        // Storage unavailable (private mode etc.) – consent still applies for this visit.
      }
    }
    setState({ granted: true, remembered: remember || state.remembered })
  }

  const revoke = () => {
    try {
      window.localStorage.removeItem(StorageKeys.MapConsent)
    } catch (error) {
      // Nothing stored that could be removed.
    }
    setState({ granted: false, remembered: false })
  }

  return { grant, granted, remembered, revoke }
}
